import { lazy, Suspense, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import { seasonRange } from '@selflevel/domain';
import { Mark } from '@selflevel/ui';
import {
  useIdentities,
  useMilestoneUnlocks,
  useProgression,
  useSealedSeasons,
  useSettings,
  useToday,
} from '../store/hooks';
import { Card, Empty, Sello, fechaLarga } from '../ui/primitivas';
import { Divisa } from '../ui/iconos';
import { indiceDeTramo, tramoAlcanzado } from './camino';
import { SECUNDARIOS } from '../ui/destinos';
import { HechoPor } from '../ui/HechoPor';

const TarjetaVitrina = lazy(() => import('./vitrina').then((m) => ({ default: m.TarjetaVitrina })));
const TarjetaMemento = lazy(() =>
  import('./memento-mori').then((m) => ({ default: m.TarjetaMemento })),
);

/**
 * PERFIL — el sitio de lo cerrado.
 *
 * Progreso es el presente; aquí vive el pasado que ya no cambia: las temporadas
 * selladas, los hitos con su fecha y el rewind de cada año. Por eso la fila de
 * «tu año» va literalmente debajo de sus cuatro sellos.
 *
 * La sala de trofeos se lee como **historial de constancia, no como marcador**.
 * No hay «mejor temporada», ni total, ni comparación con nadie: repetir Firme
 * cuatro trimestres seguidos es el logro, no el fracaso.
 */
export function Perfil(): ReactElement {
  const settings = useSettings();
  const progression = useProgression();
  const seasons = useSealedSeasons();
  const unlocks = useMilestoneUnlocks();
  const identities = useIdentities();
  const today = useToday();

  const anios = [...new Set((seasons ?? []).map((s) => s.key.slice(0, 4)))].sort().reverse();

  return (
    <main className="screen">
      <header className="topbar glass-2">
        <h1 className="t-title-3">Perfil</h1>
        <Link to="/ajustes" className="btn btn--sm btn--ghost">
          Ajustes
        </Link>
      </header>

      <section className="row">
        <Mark size={56} />
        <div className="grow">
          <p className="t-title-2">{settings.displayName || 'Tú'}</p>
          {progression ? (
            <p className="row t-sm t-dim" style={{ gap: 'var(--sp-1)' }}>
              <Divisa tramo={tramoAlcanzado(progression).key} size={16} />
              {tramoAlcanzado(progression).name} ·{' '}
              {indiceDeTramo(tramoAlcanzado(progression).key) + 1} de 5 · nivel{' '}
              {progression.generalLevel}
            </p>
          ) : null}
        </div>
      </section>

      {/* SALA DE TROFEOS */}
      <Card title="Sala de trofeos">
        {seasons === undefined ? null : seasons.length === 0 ? (
          <div className="stack">
            <div className="row">
              <Sello fill={progression?.season.liga.fill ?? 0} size={56} empty />
              <div className="grow">
                <p className="t-body-strong">
                  {progression?.season.label ?? seasonRange(today.slice(0, 4) + '-Q1').label}
                </p>
                <p className="t-sm t-dim">en curso</p>
              </div>
            </div>
            <p className="t-sm t-dim">
              Tu primera temporada está en curso. Cuando cierre, el{' '}
              {progression ? fechaLarga(progression.season.to) : ''}, se guarda aquí con la liga que
              alcances. No se pierde nada.
            </p>
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
                gap: 'var(--sp-3)',
              }}
            >
              {seasons.map((season) => (
                <div
                  key={season.key}
                  className="stack stack--tight"
                  style={{ alignItems: 'center' }}
                >
                  <Sello
                    fill={fillOf(season.ligaKey)}
                    size={52}
                    label={`${season.label}: ${season.ligaName}`}
                  />
                  <span className="t-caption t-dim" style={{ textAlign: 'center' }}>
                    {season.label.replace(' ', ' ')}
                  </span>
                  <span className="t-caption">{season.ligaName}</span>
                </div>
              ))}
              {progression ? (
                <div className="stack stack--tight" style={{ alignItems: 'center' }}>
                  <Sello fill={progression.season.liga.fill} size={52} empty />
                  <span className="t-caption t-dim" style={{ textAlign: 'center' }}>
                    en curso
                  </span>
                  <span className="t-caption">{progression.season.liga.name}</span>
                </div>
              ) : null}
            </div>
            <p className="t-sm t-dim" style={{ marginTop: 'var(--sp-4)' }}>
              {lectura(seasons.map((s) => s.ligaName))}
            </p>
          </>
        )}
      </Card>

      {/* LA VITRINA (bloque 2): los trofeos de metas y sueños, una balda por año. */}
      <Suspense fallback={null}>
        <TarjetaVitrina />
      </Suspense>

      {/* EL REWIND, debajo de los sellos: pasado cerrado con pasado cerrado. */}
      <Card title="Tu año">
        {anios.length === 0 ? (
          <p className="t-sm t-dim">
            El rewind de {today.slice(0, 4)} te espera en enero. Lo que registres hasta entonces ya
            cuenta.
          </p>
        ) : (
          <ul className="stack stack--tight">
            {anios.map((anio) => (
              <li key={anio}>
                <Link
                  to="/rewind/$anio"
                  params={{ anio }}
                  className="row row--between fila-enlace"
                  style={{ minHeight: 44 }}
                >
                  <span className="t-body-strong tnum">{anio}</span>
                  <span className="t-sm t-dim">ver tu año</span>
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/rewind/$anio"
                params={{ anio: today.slice(0, 4) }}
                className="row row--between fila-enlace"
                style={{ minHeight: 44 }}
              >
                <span className="t-body-strong tnum">{today.slice(0, 4)}</span>
                <span className="t-sm t-dim">en curso</span>
              </Link>
            </li>
          </ul>
        )}
      </Card>

      {/* MEMENTO MORI: las semanas vividas también son pasado que ya no cambia. */}
      <Suspense fallback={null}>
        <TarjetaMemento hoy={today} />
      </Suspense>

      {/* IDENTIDADES */}
      {identities && identities.length > 0 ? (
        <Card title="Quién dijiste que eras">
          <ul className="stack stack--tight">
            {identities.map((identity) => (
              <li key={identity.id} className="t-body-strong" style={{ minHeight: 32 }}>
                «{identity.text}»
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/* REGISTRO DE HITOS — aparece con el hito 1, que es justo lo que desbloquea */}
      {unlocks && unlocks.length > 0 ? (
        <Card title="Hitos" action={<span className="t-sm t-dim tnum">{unlocks.length}</span>}>
          <ul className="stack stack--tight">
            {[...unlocks].reverse().map((unlock) => (
              <li key={unlock.key} className="row row--between" style={{ minHeight: 44 }}>
                <span className="t-sm">
                  <span className="tnum t-dim">{unlock.n}</span> · {unlock.name}
                </span>
                <span className="t-sm t-dim">{fechaLarga(unlock.localDate)}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : (
        <Card title="Hitos">
          <Empty title="Todavía ninguno">
            El primero llega a los 250 puntos. A 30 al día, unos ocho días.
          </Empty>
        </Card>
      )}

      {/*
        ═══════════════════════════════════════════════════════════════════════
        Esto eran cinco botones fantasma idénticos, uno debajo de otro, con una
        palabra cada uno. No era una sección: era **un cajón**, y un cajón es lo
        que aparece cuando nadie ha decidido dónde va cada cosa. Que estuvieran
        en Perfil no era una decisión de estructura —el diario no tiene nada que
        ver con la sala de trofeos—, era el sitio que quedaba libre.

        Siguen aquí en móvil, porque la barra tiene cinco destinos y esos cinco
        están bien elegidos. Pero ahora cada uno dice para qué sirve, que es la
        diferencia entre una lista de nombres y una lista de sitios a los que se
        va por algo. En escritorio ya no hace falta pasar por aquí: el riel los
        tiene todos a la vista.
        ═══════════════════════════════════════════════════════════════════════
      */}
      <Card title="Además" className="perfil__ademas">
        <ul className="stack stack--tight">
          {SECUNDARIOS.map(({ to, label, Icon, nota }) => (
            <li key={to}>
              <Link to={to} className="row fila-enlace" style={{ minHeight: 52 }}>
                <span className="habit__disc habit__disc--hueco" style={{ width: 32, height: 32 }}>
                  <Icon size={18} />
                </span>
                <span className="grow">
                  <span className="t-body-strong">{label}</span>
                  <span className="t-sm t-dim" style={{ display: 'block' }}>
                    {nota}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Card>

      <HechoPor />
    </main>
  );
}

function fillOf(ligaKey: string): number {
  const fills: Record<string, number> = {
    tenue: 0.12,
    regular: 0.3,
    firme: 0.5,
    plena: 0.72,
    colmada: 1,
  };
  return fills[ligaKey] ?? 0.12;
}

/**
 * Una línea de lectura, no un marcador. «Seis temporadas seguidas · Firme en
 * cuatro, Regular en dos» dice constancia; «mejor: Plena» diría competición.
 */
function lectura(ligas: readonly string[]): string {
  if (ligas.length === 0) return '';
  const cuenta = new Map<string, number>();
  for (const liga of ligas) cuenta.set(liga, (cuenta.get(liga) ?? 0) + 1);

  const partes = [...cuenta.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([liga, n]) => `${liga} en ${n === 1 ? 'una' : n}`);

  const total = ligas.length === 1 ? 'Una temporada' : `${ligas.length} temporadas seguidas`;
  return `${total} · ${partes.join(', ')}.`;
}
